// Quick guide — Stress & written accents (A1). Where the stress usually falls,
// when you actually write the accent, and the special cases (words stressed
// further back, diacritics on monosyllables, grave vs acute, question words).
// Hand-drawn paper style with audio on examples.
import { speakButtonHTML } from "../lib/audio.js";

function ex(words) {
  return `<div class="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">${words
    .map((w) => `<span class="text-sage-800">${w}${speakButtonHTML(w.replace(/\s*\(.*/, ""))}</span>`)
    .join("")}</div>`;
}

export function stressAccentsView(app) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Stress & Accents à</h2>
      <p class="text-sage-600 mb-4" style="font-family:Inter,sans-serif">Italian stress usually falls on the second-to-last syllable, but it isn't fully rule-based. The written accent (à è ì ò ù) only ever marks a word stressed on its <i>final</i> vowel.</p>
      <div class="paper sketch-box p-5 sm:p-7 font-hand text-lg space-y-5">

        <div>
          <h3 class="text-2xl text-emerald-700 sketch-underline inline-block">1 · The default — penultimate stress</h3>
          <p class="text-sage-700 mt-1">Most words stress the <b>second-to-last</b> syllable, and it is <b>never written</b>.</p>
          ${ex(["casa", "amico", "ragazzo", "parlare", "signora"])}
        </div>

        <div>
          <h3 class="text-2xl text-amber-700 sketch-underline inline-block">2 · Final stress — you write the accent</h3>
          <p class="text-sage-700 mt-1">When the stress falls on the <b>last vowel</b>, the accent is <b>required</b>.</p>
          ${ex(["città", "caffè", "perché", "però", "virtù", "lunedì"])}
        </div>

        <div>
          <h3 class="text-2xl text-sky-700 sketch-underline inline-block">3 · Stress further back — never written</h3>
          <p class="text-sage-700 mt-1">Many words stress the <b>third-from-last</b> syllable (or earlier). Italian doesn't mark this — you learn it by ear.</p>
          ${ex(["tavolo", "musica", "telefono", "rapido", "abitano"])}
        </div>

        <div>
          <h3 class="text-2xl text-rose-600 sketch-underline inline-block">4 · Accents that change meaning</h3>
          <p class="text-sage-700 mt-1">On a few one-syllable words, the accent flips the meaning:</p>
          <div class="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-base">
            <span><b>è</b> is · <b>e</b> and</span>
            <span><b>dà</b> gives · <b>da</b> from</span>
            <span><b>sì</b> yes · <b>si</b> oneself</span>
            <span><b>là</b> there · <b>la</b> the</span>
            <span><b>né</b> nor · <b>ne</b> of it</span>
            <span><b>sé</b> himself · <b>se</b> if</span>
            <span><b>tè</b> tea · <b>te</b> you</span>
          </div>
        </div>

        <div>
          <h3 class="text-2xl text-violet-700 sketch-underline inline-block">5 · Grave vs acute</h3>
          <p class="text-sage-700 mt-1">Most accents are <b>grave</b> (à è ì ò ù). The <b>acute</b> (é) marks a closed <i>e</i> — chiefly in <b>-ché</b> words and in <b>né</b>, <b>sé</b>.</p>
          ${ex(["perché", "né", "sé", "caffè", "cioè", "però"])}
        </div>

        <div>
          <h3 class="text-2xl text-teal-700 sketch-underline inline-block">6 · Question words take no accent</h3>
          <p class="text-sage-700 mt-1">Unlike Spanish, Italian question words are <b>bare</b> — only <b>perché</b> carries one, because its stress is final.</p>
          ${ex(["che", "chi", "come", "dove", "quando", "quanto"])}
        </div>

      </div>
      <p class="text-xs text-sage-400 mt-4" style="font-family:Inter,sans-serif">Rule of thumb: you only ever write an accent on the <i>last</i> vowel of a word — never in the middle. Stress inside a word is something you learn by ear.</p>
    </section>`;
  window.scrollTo({ top: 0, behavior: "smooth" });
}
